/**
 * Single source of truth for the institution's MERCHANT PRICING configuration.
 *
 * The merchant transaction views (Sales list + transaction detail) must READ
 * this config — they must never define their own pricing model or visibility
 * rules. This mirrors the hand-authored `brand.ts` pattern so the pricing
 * contract is stable and not regenerated by platform tooling.
 *
 * Two independent axes live here:
 *  1. pricingModel  — how the merchant is PRICED (IC++, IC+, or blended).
 *  2. visibility    — how RICH the statement/export is (Basic … Enterprise).
 *
 * IMPORTANT: visibility controls EXPORT richness and whether the drill-down /
 * transaction-level export is available. It does NOT make the default UI
 * denser — every merchant sees the same clean, summarised default view.
 */

/* ------------------------------------------------------------------ *
 * Pricing model + statement visibility
 * ------------------------------------------------------------------ */

export type PricingModel = 'ic_plus_plus' | 'ic_plus' | 'blended'

export type StatementVisibility =
  | 'basic'
  | 'standard'
  | 'detailed_ic'
  | 'enterprise'

export interface MerchantPricing {
  pricingModel: PricingModel
  visibility: StatementVisibility
}

/**
 * The configured pricing profile for the current merchant. Change here to
 * re-profile the whole prototype (e.g. flip to a blended merchant).
 */
export const merchantPricing: MerchantPricing = {
  pricingModel: 'ic_plus_plus',
  visibility: 'detailed_ic',
}

/** Whether the full per-component export + on-demand drill-down are available. */
export function isDrillDownAvailable(
  visibility: StatementVisibility = merchantPricing.visibility,
): boolean {
  return visibility === 'detailed_ic' || visibility === 'enterprise'
}

/** Export richness keyed off visibility. Basic = totals only. */
export function exportRichness(
  visibility: StatementVisibility = merchantPricing.visibility,
): 'totals_only' | 'standard' | 'full_ledger' {
  if (visibility === 'basic') return 'totals_only'
  if (visibility === 'standard') return 'standard'
  return 'full_ledger'
}

/* ------------------------------------------------------------------ *
 * Payment-method types + canonical fee taxonomy
 * ------------------------------------------------------------------ */

/**
 * Payment method type drives WHICH fee lines may appear on a transaction.
 * Apple Pay / Google Pay carry no own model — their fees follow the
 * underlying instrument the token resolves to (usually a card).
 */
export type PaymentMethodType =
  | 'card'
  | 'wallet'
  | 'qr'
  | 'bank_transfer'
  | 'bnpl'
  | 'apple_pay'
  | 'google_pay'

/**
 * Fixed machine identifier for every fee line. NEVER shown to the merchant.
 * All grouping, totalling, and exports key on this code, not the label.
 */
export type FeeCode =
  | 'IC_INTERCHANGE'
  | 'SCHEME_FEE_TXN'
  | 'SCHEME_FEE_PERIOD'
  | 'MARKUP'
  | 'BLENDED_MDR'
  | 'PROVIDER_FEE'
  | 'FIXED_TXN_FEE'
  | 'GATEWAY_FEE'
  | 'FX_COST'
  | 'PROCESSOR_FEE'
  | 'DISPUTE_COST'
  | 'MIN_FEE_TOPUP'
  | 'CHARGEBACK_FEE_CHARGED'
  | 'REFUND_FEE_CHARGED'

/**
 * Human label for each fee code. Two hard rules baked in:
 *  - MARKUP (margin only) and BLENDED_MDR (all-in price) are never the same.
 *  - The ambiguous label "Payment method fee" is never used.
 */
export const FEE_DISPLAY_NAMES: Record<FeeCode, string> = {
  IC_INTERCHANGE: 'Interchange',
  SCHEME_FEE_TXN: 'Scheme fee',
  SCHEME_FEE_PERIOD: 'Scheme fee (period allocation)',
  MARKUP: 'Acquirer markup',
  BLENDED_MDR: 'Blended processing rate (MDR)',
  PROVIDER_FEE: 'Provider fee',
  FIXED_TXN_FEE: 'Fixed transaction fee',
  GATEWAY_FEE: 'Gateway fee',
  FX_COST: 'FX cost',
  PROCESSOR_FEE: 'Processor fee',
  DISPUTE_COST: 'Dispute fee',
  MIN_FEE_TOPUP: 'Minimum fee top-up',
  CHARGEBACK_FEE_CHARGED: 'Chargeback fee',
  REFUND_FEE_CHARGED: 'Refund fee',
}

export function feeDisplayName(code: FeeCode): string {
  return FEE_DISPLAY_NAMES[code] ?? code
}

/**
 * The fixed fee model per payment-method type: the set of fee codes that may
 * legitimately appear on a transaction of that type. Fee lines outside a
 * type's model must never be shown (e.g. no interchange on a QR or wallet
 * transaction). Apple Pay / Google Pay inherit the card model because the
 * token resolves to a card instrument.
 */
export const FEE_MODEL_BY_METHOD: Record<PaymentMethodType, FeeCode[]> = {
  card: [
    'IC_INTERCHANGE',
    'SCHEME_FEE_TXN',
    'SCHEME_FEE_PERIOD',
    'PROCESSOR_FEE',
    'FX_COST',
    'MARKUP',
    'BLENDED_MDR',
    'FIXED_TXN_FEE',
    'GATEWAY_FEE',
    'DISPUTE_COST',
    'CHARGEBACK_FEE_CHARGED',
    'REFUND_FEE_CHARGED',
  ],
  apple_pay: [
    'IC_INTERCHANGE',
    'SCHEME_FEE_TXN',
    'SCHEME_FEE_PERIOD',
    'PROCESSOR_FEE',
    'FX_COST',
    'MARKUP',
    'BLENDED_MDR',
    'FIXED_TXN_FEE',
    'GATEWAY_FEE',
    'DISPUTE_COST',
    'CHARGEBACK_FEE_CHARGED',
    'REFUND_FEE_CHARGED',
  ],
  google_pay: [
    'IC_INTERCHANGE',
    'SCHEME_FEE_TXN',
    'SCHEME_FEE_PERIOD',
    'PROCESSOR_FEE',
    'FX_COST',
    'MARKUP',
    'BLENDED_MDR',
    'FIXED_TXN_FEE',
    'GATEWAY_FEE',
    'DISPUTE_COST',
    'CHARGEBACK_FEE_CHARGED',
    'REFUND_FEE_CHARGED',
  ],
  wallet: [
    'PROVIDER_FEE',
    'FX_COST',
    'MARKUP',
    'FIXED_TXN_FEE',
    'REFUND_FEE_CHARGED',
  ],
  qr: ['PROVIDER_FEE', 'MARKUP', 'FIXED_TXN_FEE', 'REFUND_FEE_CHARGED'],
  bank_transfer: [
    'PROVIDER_FEE',
    'FX_COST',
    'MARKUP',
    'FIXED_TXN_FEE',
    'REFUND_FEE_CHARGED',
  ],
  bnpl: ['PROVIDER_FEE', 'MARKUP', 'REFUND_FEE_CHARGED'],
}

/** Whether a fee code is valid for a given payment method's model. */
export function isFeeAllowedForMethod(
  code: FeeCode,
  method: PaymentMethodType,
): boolean {
  return FEE_MODEL_BY_METHOD[method]?.includes(code) ?? false
}

/* ------------------------------------------------------------------ *
 * Clean default summary grouping
 * ------------------------------------------------------------------ *
 * The default transaction UI shows a SHORT summary — never the full
 * per-component IC++ breakdown, for ANY merchant (incl. Enterprise).
 *
 * Each summary line declares which fee codes roll up into it. The view sums
 * the matching posted+pending fee rows by fee_code and renders one line.
 */

export type CalcStatus = 'Estimated' | 'Actual' | 'Reconciled'

export interface SummaryLineDef {
  /** Stable key for the rolled-up line. */
  key: string
  /** Human label shown in the default summary. */
  label: string
  /** Fee codes that roll up into this single line. */
  feeCodes: FeeCode[]
}

/**
 * Returns the clean default-summary line definitions for a given method +
 * pricing model. This is the ONLY grouping the default UI should render.
 *
 *  - IC++ card  -> one "Pass-through (interchange + scheme)" + one "Acquirer fee"
 *  - blended    -> one "Processing fee"
 *  - wallet/qr/a2a/bnpl -> "Provider fee" + "Acquirer fee"
 */
export function getDefaultSummaryLines(
  method: PaymentMethodType,
  pricingModel: PricingModel = merchantPricing.pricingModel,
): SummaryLineDef[] {
  const isCardLike =
    method === 'card' || method === 'apple_pay' || method === 'google_pay'

  if (isCardLike) {
    if (pricingModel === 'blended') {
      return [
        {
          key: 'processing_fee',
          label: 'Processing fee',
          feeCodes: [
            'BLENDED_MDR',
            'FIXED_TXN_FEE',
            'GATEWAY_FEE',
            'FX_COST',
            'PROCESSOR_FEE',
          ],
        },
      ]
    }
    // IC++ / IC+ : single-level split only
    return [
      {
        key: 'pass_through',
        label: 'Pass-through (interchange + scheme)',
        feeCodes: [
          'IC_INTERCHANGE',
          'SCHEME_FEE_TXN',
          'SCHEME_FEE_PERIOD',
          'PROCESSOR_FEE',
        ],
      },
      {
        key: 'acquirer_fee',
        label: 'Acquirer fee',
        feeCodes: ['MARKUP', 'FIXED_TXN_FEE', 'GATEWAY_FEE', 'FX_COST'],
      },
    ]
  }

  // Wallet / QR / A2A / BNPL
  return [
    {
      key: 'provider_fee',
      label: 'Provider fee',
      feeCodes: ['PROVIDER_FEE', 'FX_COST'],
    },
    {
      key: 'acquirer_fee',
      label: 'Acquirer fee',
      feeCodes: ['MARKUP', 'FIXED_TXN_FEE'],
    },
  ]
}

/** Short explainer for fees that post in arrears (scheme-related). */
export const ESTIMATED_FEE_EXPLAINER =
  'Scheme-related fees are confirmed when the network bills them, typically 1–2 days after the payment.'
